import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { BiData } from 'react-icons/bi';
import AppContext from 'Utils/AppContext';
import FileTypeChip from 'DataSources/file/FileTypeChip';

const RUNNING_STATES = new Set(['RUNNING', 'QUEUED']);

/**
 * Children of one data source (rows whose parentDataSourceId points at it) — the
 * sub-sources spawned from folder-search candidates, the profile folder-discover flow,
 * or "Discover everything" auto fan-out. Sub-sources are hidden from the top-level
 * Data Sources table by default, so this panel is where they live: rendered in the
 * source editor and the risk-profile hub. All data is client-side (children are
 * already in the globally-loaded connections array); large broods scroll.
 */
const SubSourcesPanel = ({ parentId, maxHeight = 320 }) => {
  const { connections, searchStatuses } = useContext(AppContext);
  const [searchField, setSearchField] = useState('');

  const children = useMemo(
    () => (connections || []).filter((c) => c && c.parentDataSourceId === parentId),
    [connections, parentId]
  );

  const runningSourceIds = useMemo(() => {
    const set = new Set();
    (searchStatuses || []).forEach((s) => {
      if (s && s.dataSourceId != null && RUNNING_STATES.has(s.state || s.glue_status)) {
        set.add(Number(s.dataSourceId));
      }
    });
    return set;
  }, [searchStatuses]);

  if (!parentId || children.length === 0) return null;

  const term = searchField.trim().toLowerCase();
  const visible = term
    ? children.filter((c) =>
        String(c.name || '').toLowerCase().includes(term)
        || String(c.rootPath || c.config?.rootPath || '').toLowerCase().includes(term))
    : children;

  return (
    <Card className='shadow-sm widget-card mb-4' data-testid='sub-sources-panel'>
      <div className='widget-header d-flex align-items-center p-3' style={{ marginBottom: 0, gap: 8, flexWrap: 'wrap' }}>
        <BiData className='mr-1 widget-header-icon' />
        <span>Sub-sources ({children.length})</span>
        {children.length > 8 && (
          <input
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            placeholder='Search sub-sources…'
            aria-label='Search sub-sources'
            style={{
              marginLeft: 'auto', minWidth: 180, borderRadius: 8, border: '1px solid #E5E8ED',
              background: '#fff', padding: '4px 10px', fontSize: 13, outline: 'none', fontFamily: 'Rubik',
            }}
          />
        )}
      </div>
      <div className='px-3 pb-2' style={{ fontSize: 12, color: '#8A92A0', fontFamily: 'Rubik' }}>
        Created from this source's folder search or profile. Each runs and reports on its own;
        they are hidden from the main Data Sources list.
      </div>
      <div style={{ maxHeight, overflowY: 'auto' }}>
        <table style={{ width: '100%', fontFamily: 'Rubik', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#fff', textAlign: 'left', color: '#5C6473' }}>
              <th style={{ padding: '6px 16px', fontWeight: 600 }}>Name</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Type</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Folder</th>
              <th style={{ padding: '6px 8px', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '6px 16px' }} />
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const rootPath = c.rootPath || c.config?.rootPath || '';
              const isRunning = runningSourceIds.has(Number(c.id));
              return (
                <tr key={c.id} style={{ borderTop: '1px solid #EDEFF2' }}>
                  <td style={{ padding: '6px 16px' }}>
                    <Link to={`/sources/source-editor/${encodeURIComponent(c.id)}`}>{c.name}</Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <FileTypeChip type={c.datasourceType || c.fileType} />
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }} title={rootPath}>
                    {rootPath.length > 48 ? `…${rootPath.slice(-47)}` : rootPath}
                  </td>
                  <td style={{ padding: '6px 8px', color: isRunning ? '#1E40AF' : '#8A92A0' }}>
                    {isRunning ? 'Running…' : '—'}
                  </td>
                  <td style={{ padding: '6px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Link to={`/landscape/riskprofile/${encodeURIComponent(c.id)}`}>
                      Risk profile →
                    </Link>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '10px 16px', color: '#8A92A0' }}>
                  No sub-sources match “{searchField.trim()}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default SubSourcesPanel;
